// Globale Variable für den API-Ladestatus
let googleMapsLoadingPromise: Promise<void> | null = null;

// Hilfsfunktion zum Überprüfen, ob die API vollständig geladen ist
const isGoogleMapsFullyLoaded = () => {
  return window.google?.maps?.Map instanceof Function &&
         window.google?.maps?.places?.Autocomplete instanceof Function;
};

// Zentrale Funktion zum Laden der Google Maps API
export async function loadGoogleMapsApi(): Promise<void> {
  // Wenn die API bereits geladen wird oder geladen ist, gib die existierende Promise zurück
  if (googleMapsLoadingPromise) {
    return googleMapsLoadingPromise;
  }

  // Wenn die API bereits vollständig im Window-Objekt verfügbar ist
  if (isGoogleMapsFullyLoaded()) {
    console.log('Google Maps API bereits vollständig geladen');
    return Promise.resolve();
  }

  // Erstelle eine neue Promise für das Laden der API
  googleMapsLoadingPromise = new Promise(async (resolve, reject) => {
    try {
      // API-Key von der Konfiguration abrufen
      const response = await fetch('/api/config');
      const config = await response.json();
      const apiKey = config.googleMapsApiKey;

      if (!apiKey) {
        throw new Error('Kein Google Maps API-Key gefunden');
      }

      // Script-Tag erstellen und einfügen
      const script = document.createElement('script');
      
      // Moderne API-Ladung mit loading=async Parameter
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=weekly&loading=async`;
      script.async = true;
      script.defer = true;

      // Event-Listener für erfolgreichen Load
      script.onload = () => {
        // Warte bis die API wirklich verfügbar ist
        const checkForMaps = setInterval(() => {
          if (isGoogleMapsFullyLoaded()) {
            clearInterval(checkForMaps);
            console.log('Google Maps API erfolgreich geladen');
            resolve();
          }
        }, 100);

        // Timeout nach 10 Sekunden
        setTimeout(() => {
          clearInterval(checkForMaps);
          if (!isGoogleMapsFullyLoaded()) {
            console.error('Timeout beim Laden der Google Maps API');
            reject(new Error('Timeout beim Laden der Google Maps API'));
          }
        }, 10000);
      };

      // Event-Listener für Fehler
      // P0.5: ERR_BLOCKED_BY_CLIENT (Adblock) darf nicht crashen
      script.onerror = (error) => {
        console.warn('Google Maps API konnte nicht geladen werden (möglicherweise durch Adblocker blockiert):', error);
        googleMapsLoadingPromise = null; // Reset für erneuten Versuch
        // Graceful rejection - keine unhandled promise rejection
        reject(new Error('Google Maps API konnte nicht geladen werden (möglicherweise blockiert)'));
      };

      // Script zum DOM hinzufügen
      document.head.appendChild(script);
    } catch (error) {
      console.error('Fehler beim Laden der Google Maps API:', error);
      googleMapsLoadingPromise = null; // Reset für erneuten Versuch
      reject(error);
    }
  });

  return googleMapsLoadingPromise;
}

// Typen für globales Window-Objekt
declare global {
  interface Window {
    google: any;
  }
} 